
  let mapsLoadingPromise = null;
  const CHANGE_DEBOUNCE_MS = 200;
  const EDITABLE_STYLE = {
    strokeColor: "#1a73e8",
    strokeWeight: 2,
    fillColor: "#1a73e8",
    fillOpacity: 0.25
  };

  function loadGoogleMaps(apiKey) {
    if (window.google && window.google.maps) {
      return Promise.resolve();
    }
    if (mapsLoadingPromise) {
      return mapsLoadingPromise;
    }
    mapsLoadingPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      // NOTE: no `libraries=drawing`. Google removed DrawingManager in Maps
      // JS 3.65, and merely *requesting* the drawing library now throws an
      // uncaught deprecation error on every map — editable or not. The
      // editable layer below draws with plain Marker/Polygon + map click
      // handlers instead, so the library is not needed at all.
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=__gmapsInit`;
      script.async = true;
      script.defer = true;
      window.__gmapsInit = () => {
        delete window.__gmapsInit;
        resolve();
      };
      script.onerror = () => {
        mapsLoadingPromise = null;
        reject(new Error("Failed to load Google Maps API"));
      };
      document.head.appendChild(script);
    });
    return mapsLoadingPromise;
  }

  export default {
    async mounted() {
      this.isDestroyed = false;
      const apiKey = this.el.dataset.apiKey;
      const center = JSON.parse(this.el.dataset.center);
      const zoom = parseInt(this.el.dataset.zoom, 10);
      const mapType = this.el.dataset.mapType;
      const markersData = JSON.parse(this.el.dataset.markers);
      const polygonsData = JSON.parse(this.el.dataset.polygons);
      const editable = this.el.dataset.editable === "true";

      // Initialise collections up-front so a server event that arrives
      // before the async Maps load resolves is handled (as a no-op) rather
      // than dropped.
      this.markers = [];
      this.infoWindows = [];
      this.polygons = [];
      // Polygons the server drew after mount, by id (add_map_polygons/3).
      this.runtimePolygons = new Map();
      // Add/remove commands that arrived before the Maps script loaded.
      // Unlike a toggle, an add must not be dropped: it is the only time
      // the server sends that polygon.
      this.pendingPolygonCommands = [];

      this.handleEvent(`add-polygons:${this.el.id}`, ({ polygons }) =>
        this.polygonCommand("add", polygons || [])
      );
      this.handleEvent(`remove-polygons:${this.el.id}`, ({ ids }) =>
        this.polygonCommand("remove", ids || [])
      );

      this.handleEvent(`toggle-polygon-group:${this.el.id}`, ({ group, visible }) => {
        this.polygons.forEach(({ polygon, group: g }) => {
          if (g === group) polygon.setMap(visible ? this.map : null);
        });
      });

      try {
        await loadGoogleMaps(apiKey);
      } catch (e) {
        if (this.isDestroyed) return;
        this.el.textContent = "Map could not be loaded.";
        this.el.classList.add("reg-map-error");
        return;
      }

      // The LiveView may have navigated away while the Maps script loaded;
      // if destroyed() already ran, bail before building a detached map.
      if (this.isDestroyed) return;

      this.map = new google.maps.Map(this.el, {
        center: center,
        zoom: zoom,
        mapTypeId: mapType,
        disableDefaultUI: true,
        zoomControl: true
      });

      markersData.forEach((m) => {
        const markerOpts = { position: m.position, map: this.map };
        if (m.title) markerOpts.title = m.title;
        if (m.icon) markerOpts.icon = m.icon;
        if (m.z_index != null) markerOpts.zIndex = m.z_index;

        const marker = new google.maps.Marker(markerOpts);
        this.markers.push(marker);

        const tpl = document.getElementById(m.info_window_id);
        const hasInfoContent = tpl && tpl.innerHTML.trim().length > 0;
        let infoWindow = null;

        if (hasInfoContent) {
          infoWindow = new google.maps.InfoWindow({
            content: tpl.innerHTML
          });
          this.infoWindows.push(infoWindow);
        }

        marker.addListener("click", () => {
          if (infoWindow) {
            this.infoWindows.forEach(iw => iw.close());
            infoWindow.open(this.map, marker);
          }
          if (m.on_click) {
            this.pushToHost(m.on_click, m.on_click_value || {});
          }
        });
      });

      polygonsData.forEach((p) => {
        // Attribute polygons keep Google's default and catch clicks.
        const polygon = this.buildPolygon(p, true);
        this.polygons.push({ polygon, group: p.group || null });
      });

      const pending = this.pendingPolygonCommands;
      this.pendingPolygonCommands = [];
      pending.forEach(([kind, arg]) => this.polygonCommand(kind, arg));

      if (editable) {
        if (this.isDestroyed) return;
        this.setupEditable();
      }
    },

    setupEditable() {
      this.onChangeEvent = this.el.dataset.onChange;
      this.singlePoint = this.el.dataset.singlePoint === "true";
      this.mode = "pan";
      this.editableMarkers = [];
      this.editablePolygon = null;
      this.changeTimer = null;
      this.draftVertices = [];
      this.draftPolygon = null;
      this.draftMarkers = [];

      // Drawing is driven by a single map click listener that dispatches on
      // the current mode (see setMode). This replaces DrawingManager, which
      // Google removed in Maps JS 3.65.
      this.mapClickListener = this.map.addListener("click", (e) => {
        if (!e.latLng) return;
        if (this.mode === "point") {
          // A single-point map swaps its pin: the old one goes silently,
          // so the host sees one change holding one point.
          if (this.singlePoint) this.removeEditablePoints();
          const marker = new google.maps.Marker({
            position: e.latLng,
            map: this.map,
            draggable: true
          });
          this.registerEditablePoint(marker);
          this.flushChange();
        } else if (this.mode === "polygon") {
          this.addDraftVertex(e.latLng);
        }
      });

      const seed = JSON.parse(this.el.dataset.userGeometry || "{}");
      const seedPoints = seed.points || [];
      (this.singlePoint ? seedPoints.slice(-1) : seedPoints).forEach((p) => {
        const marker = new google.maps.Marker({ position: p, map: this.map, draggable: true });
        this.registerEditablePoint(marker);
      });
      if (seed.polygon && seed.polygon.length) {
        const polygon = new google.maps.Polygon(
          Object.assign(
            { paths: seed.polygon, editable: true, draggable: true, map: this.map },
            EDITABLE_STYLE
          )
        );
        this.registerEditablePolygon(polygon);
      }

      this.toolbar = document.getElementById(`${this.el.id}-toolbar`);
      if (this.toolbar) {
        this.toolbarHandler = (e) => {
          const btn = e.target.closest("[data-mode],[data-action]");
          if (!btn || !this.toolbar.contains(btn)) return;
          if (btn.dataset.mode) {
            this.setMode(btn.dataset.mode);
          } else if (btn.dataset.action === "clear-points") {
            this.clearPoints();
          } else if (btn.dataset.action === "clear-polygon") {
            this.clearPolygon();
          }
        };
        this.toolbar.addEventListener("click", this.toolbarHandler);
      }

      // The toolbar renders a button only for an offered tool, so a mode
      // with no button is not offered and falls back to pan.
      const wanted = this.el.dataset.initialMode || "pan";
      const offered =
        this.toolbar && this.toolbar.querySelector(`[data-mode="${wanted}"]`);
      this.setMode(offered ? wanted : "pan");
    },

    registerEditablePoint(marker) {
      this.editableMarkers.push(marker);
      marker.addListener("dragend", () => this.flushChange());
      marker.addListener("click", () => {
        if (this.mode !== "pan") return;
        google.maps.event.clearInstanceListeners(marker);
        marker.setMap(null);
        this.editableMarkers = this.editableMarkers.filter((m) => m !== marker);
        this.flushChange();
      });
    },

    registerEditablePolygon(polygon) {
      if (this.editablePolygon) {
        google.maps.event.clearInstanceListeners(this.editablePolygon.getPath());
        google.maps.event.clearInstanceListeners(this.editablePolygon);
        this.editablePolygon.setMap(null);
      }
      this.editablePolygon = polygon;
      const path = polygon.getPath();
      google.maps.event.addListener(path, "set_at", () => this.scheduleChange());
      google.maps.event.addListener(path, "insert_at", () => this.scheduleChange());
      google.maps.event.addListener(path, "remove_at", () => this.scheduleChange());
      polygon.addListener("dragend", () => this.flushChange());
    },

    // ── Polygon drafting ────────────────────────────────────────────────
    // In polygon mode each map click appends a vertex. The first vertex
    // gets a clickable marker; clicking it closes the ring (>= 3 vertices),
    // which is the same "click the start point to finish" gesture
    // DrawingManager used to provide.

    addDraftVertex(latLng) {
      this.draftVertices.push(latLng);

      if (this.draftVertices.length === 1) {
        // A circle symbol, not the default pin: Google centres a
        // SymbolPath.CIRCLE on its position, so clicking the first vertex
        // itself closes the ring. The pin is anchored at its tip, which
        // put its clickable head above the vertex, and a click on the
        // vertex fell through to the map and added another one.
        const closeMarker = new google.maps.Marker({
          position: latLng,
          map: this.map,
          title: "Click to close the polygon",
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 7,
            fillColor: "#ffffff",
            fillOpacity: 1,
            strokeColor: EDITABLE_STYLE.strokeColor,
            strokeWeight: 2
          }
        });
        closeMarker.addListener("click", () => this.completeDraftPolygon());
        this.draftMarkers.push(closeMarker);
      }

      if (!this.draftPolygon) {
        this.draftPolygon = new google.maps.Polygon(
          Object.assign({ map: this.map, clickable: false }, EDITABLE_STYLE)
        );
      }
      this.draftPolygon.setPath(this.draftVertices);
    },

    completeDraftPolygon() {
      if (this.draftVertices.length < 3) return;
      const paths = this.draftVertices.slice();
      this.discardDraftPolygon();

      const polygon = new google.maps.Polygon(
        Object.assign(
          { paths, editable: true, draggable: true, map: this.map },
          EDITABLE_STYLE
        )
      );
      this.registerEditablePolygon(polygon);
      this.setMode("pan");
      this.flushChange();
    },

    // Tears down the in-progress draft without touching the committed
    // polygon. Safe to call when there is no draft, and on non-editable
    // maps where setupEditable() never ran (destroyed() calls this
    // unconditionally).
    discardDraftPolygon() {
      if (this.draftPolygon) {
        this.draftPolygon.setMap(null);
        this.draftPolygon = null;
      }
      (this.draftMarkers || []).forEach((m) => {
        google.maps.event.clearInstanceListeners(m);
        m.setMap(null);
      });
      this.draftMarkers = [];
      this.draftVertices = [];
    },

    setMode(mode) {
      // Leaving polygon mode mid-draw abandons the unfinished ring rather
      // than silently committing a partial shape.
      if (this.mode === "polygon" && mode !== "polygon") {
        this.discardDraftPolygon();
      }
      this.mode = mode;
      if (this.map) {
        this.map.setOptions({
          draggableCursor: mode === "pan" ? null : "crosshair"
        });
      }
      if (this.toolbar) {
        this.toolbar.querySelectorAll("[data-mode]").forEach((btn) => {
          btn.classList.toggle("reg-map-tool-active", btn.dataset.mode === mode);
        });
      }
    },

    readGeometry() {
      const points = this.editableMarkers.map((m) => {
        const pos = m.getPosition();
        return { lat: pos.lat(), lng: pos.lng() };
      });
      let polygon = null;
      if (this.editablePolygon) {
        polygon = this.editablePolygon
          .getPath()
          .getArray()
          .map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));
      }
      return { id: this.el.id, points, polygon };
    },

    pushGeometry() {
      if (this.onChangeEvent) {
        this.pushToHost(this.onChangeEvent, this.readGeometry());
      }
    },

    scheduleChange() {
      if (this.changeTimer) clearTimeout(this.changeTimer);
      this.changeTimer = setTimeout(() => {
        this.changeTimer = null;
        this.pushGeometry();
      }, CHANGE_DEBOUNCE_MS);
    },

    flushChange() {
      if (this.changeTimer) {
        clearTimeout(this.changeTimer);
        this.changeTimer = null;
      }
      this.pushGeometry();
    },

    clearPoints() {
      this.removeEditablePoints();
      this.flushChange();
    },

    // Removes every editable pin without telling the server.
    removeEditablePoints() {
      this.editableMarkers.forEach((m) => {
        google.maps.event.clearInstanceListeners(m);
        m.setMap(null);
      });
      this.editableMarkers = [];
    },

    // To the element's phx-target when it has one (a LiveComponent passes
    // phx-target={@myself}, which renders as its numeric id), else to the
    // LiveView, as before.
    pushToHost(event, payload) {
      const target = this.el.getAttribute("phx-target");
      if (target) {
        this.pushEventTo(target, event, payload);
      } else {
        this.pushEvent(event, payload);
      }
    },

    buildPolygon(p, clickableDefault) {
      return new google.maps.Polygon({
        paths: p.paths,
        strokeColor: p.strokeColor || "#FF0000",
        strokeOpacity: p.strokeOpacity != null ? p.strokeOpacity : 0.8,
        strokeWeight: p.strokeWeight != null ? p.strokeWeight : 2,
        fillColor: p.fillColor || "#FF0000",
        fillOpacity: p.fillOpacity != null ? p.fillOpacity : 0.35,
        clickable: p.clickable != null ? p.clickable : clickableDefault,
        map: this.map
      });
    },

    polygonCommand(kind, arg) {
      if (!this.map) {
        this.pendingPolygonCommands.push([kind, arg]);
        return;
      }
      if (kind === "add") {
        this.addPolygons(arg);
      } else {
        this.removePolygons(arg);
      }
    },

    // Adding an id already drawn replaces it. Added polygons let clicks
    // through unless they say otherwise, so a pick inside one still works.
    addPolygons(polygons) {
      polygons.forEach((p) => {
        this.removePolygons([p.id]);
        const polygon = this.buildPolygon(p, false);
        this.runtimePolygons.set(p.id, polygon);
        this.polygons.push({ polygon, group: p.group || null });
      });
    },

    // Only added polygons have an id. An unknown id is ignored.
    removePolygons(ids) {
      ids.forEach((id) => {
        const polygon = this.runtimePolygons.get(id);
        if (!polygon) return;
        polygon.setMap(null);
        this.runtimePolygons.delete(id);
        this.polygons = this.polygons.filter((entry) => entry.polygon !== polygon);
      });
    },

    clearPolygon() {
      // An unfinished ring is drawn exactly like a finished one, so the
      // clear button removes it too. The mode is left alone: the next
      // click in polygon mode starts a new ring.
      this.discardDraftPolygon();
      if (this.editablePolygon) {
        google.maps.event.clearInstanceListeners(this.editablePolygon.getPath());
        google.maps.event.clearInstanceListeners(this.editablePolygon);
        this.editablePolygon.setMap(null);
        this.editablePolygon = null;
      }
      this.flushChange();
    },

    destroyed() {
      this.isDestroyed = true;
      if (this.changeTimer) clearTimeout(this.changeTimer);
      if (this.toolbar && this.toolbarHandler) {
        this.toolbar.removeEventListener("click", this.toolbarHandler);
      }
      if (this.mapClickListener) {
        google.maps.event.removeListener(this.mapClickListener);
        this.mapClickListener = null;
      }
      this.discardDraftPolygon();
      if (this.editablePolygon) {
        google.maps.event.clearInstanceListeners(this.editablePolygon.getPath());
        google.maps.event.clearInstanceListeners(this.editablePolygon);
        this.editablePolygon.setMap(null);
      }
      if (this.editableMarkers) {
        this.editableMarkers.forEach((m) => {
          google.maps.event.clearInstanceListeners(m);
          m.setMap(null);
        });
      }
      if (this.markers) {
        this.markers.forEach((m) => {
          google.maps.event.clearInstanceListeners(m);
          m.setMap(null);
        });
      }
      if (this.polygons) this.polygons.forEach(({ polygon }) => polygon.setMap(null));
      if (this.infoWindows) this.infoWindows.forEach(iw => iw.close());
      this.markers = [];
      this.polygons = [];
      this.runtimePolygons = new Map();
      this.pendingPolygonCommands = [];
      this.infoWindows = [];
      this.editableMarkers = [];
      this.editablePolygon = null;
      this.map = null;
    }
  }
